/**
 * Shared `is_terminal` ("Final") column and new-stage payload helper for
 * stage editors (StagesSlideout, CreatePipelineSlideout).
 *
 * Kept in one place so the two editors can't silently drift — e.g. one
 * gaining `data-form-field` scroll-to-error wiring the other lacks, or one
 * changing omit-when-false payload semantics without the other following.
 */

import { Toggle } from '@/lib/base/forms/toggle';
import { Tooltip } from '@/lib/base/tooltip/tooltip';

import type { StageColumn, StageItem } from './StageListEditor';

type StageWithTerminal = StageItem & { is_terminal?: boolean };

/**
 * Width of the "Final" column. Sized to its content — the 36px toggle plus
 * the "FINAL" header — so the column slots in at the end of the row instead
 * of reserving input-sized space the toggle never fills. The stage name is
 * the row's only growing cell; every trailing column is fixed-width.
 */
const IS_TERMINAL_COLUMN_WIDTH = 'w-14';

/** Factory for the "Final" toggle column, shared by both stage editors. */
export function createIsTerminalColumn<T extends StageWithTerminal>(
  onIsTerminalChange: (entityId: string, isTerminal: boolean) => void,
  isSaving: boolean
): StageColumn<T> {
  return {
    id: 'is_terminal',
    header: 'Final',
    width: IS_TERMINAL_COLUMN_WIDTH,
    align: 'center',
    renderCell: (stage, { disabled }) => (
      <div
        data-form-field={`${stage.id}:is_terminal`}
        className='flex justify-center'
      >
        <Tooltip
          title="Final stages end the work. Agents won't move items here without approval, and more than one stage can be final."
          placement='top'
          interactive
        >
          <Toggle
            isSelected={stage.is_terminal ?? false}
            onChange={isSelected => onIsTerminalChange(stage.id, isSelected)}
            isDisabled={disabled || isSaving}
            aria-label={
              stage.is_terminal
                ? `Mark "${stage.name || 'stage'}" as not final`
                : `Mark "${stage.name || 'stage'}" as final`
            }
          />
        </Tooltip>
      </div>
    ),
    renderDragPreview: stage => (
      <span className='text-sm text-fg-quaternary'>
        {stage.is_terminal ? 'Final' : ''}
      </span>
    ),
  };
}

/**
 * New stages only send is_terminal when true — omitting it entirely means
 * a stage the user never touched keeps `ahoy_is_terminal` unset (non-terminal)
 * rather than an unrelated edit ever clobbering it with an explicit `false`.
 */
export function omitFalseTerminalForNewStage(
  isTerminal: boolean | undefined
): { is_terminal: true } | Record<string, never> {
  return isTerminal ? { is_terminal: true } : {};
}

type StageWithTerminalChoice = {
  is_terminal?: boolean;
  /** Whether the user explicitly interacted with the Final toggle this session. */
  terminalTouched?: boolean;
};

/**
 * Build the `is_terminal` payload for a brand-new (or create-flow) stage.
 *
 * Once the user has explicitly toggled the Final control, their exact choice is
 * persisted — including an explicit `false` (e.g. unchecking a seeded-final
 * "Closed Won"). Until then, `false` is omitted so the property stays unset
 * (non-terminal) for stages the user never considered.
 */
export function buildNewStageTerminalPayload(
  stage: StageWithTerminalChoice
): { is_terminal: boolean } | Record<string, never> {
  if (stage.terminalTouched) {
    return { is_terminal: stage.is_terminal ?? false };
  }
  return omitFalseTerminalForNewStage(stage.is_terminal);
}
